/**
 * Event Details Widget - Embeddable Bundle Entry Point
 *
 * One page for woodsidebible.org/events/details/?id=<Event_ID>, replacing
 * the stock <mpp-event-details> and, with it, the trip through <mpp-checkout>
 * on /pay: the event, who in your household is going, registration, the
 * return from Online Giving and the invoice's state all live here.
 *
 * Embed:
 *   <div id="event-details-root"></div>
 *   <script src="https://widgets.woodsidebible.org/event-details/widget.js"></script>
 *
 * The event comes from `data-event` or, by default, the page's `?id=` query
 * param (what the finder links to and what the stock widget used). The same
 * page also answers `?invoice=<guid>` (a shared pay link) and OLG's return,
 * `?token=<jwt>&transactionSuccess=…`, whose invoiceId claim names the
 * invoice. `data-return-url` (stock `returnurl`) is where "All events" goes;
 * `data-details-url` is this page's own address for links to other dates.
 */

import { setAssetBase } from '../src/lib/assets';
import React from 'react';
import { EventDetails } from '../src/components/EventDetails';
import { attr } from './params';
import { ShadowWidget } from './shell';
import { announceReturnToOpener } from '../src/lib/payment';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  eventId?: number;
  finderUrl?: string;
  detailsUrl?: string;
}

declare global {
  interface Window {
    EVENT_DETAILS_WIDGET_CONFIG?: WidgetConfig;
    EventDetailsWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'event-details-root';
const LEGACY_CONTAINER_ID = 'eventDetails';

function query(): URLSearchParams {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
}

/** Lowercase, digits and hyphens — the shape `Events.Slug` is allowed to be. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\/[a-z0-9](?:[a-z0-9-]*[a-z0-9])?){0,2}$/;

/**
 * Which event this page is about, however it was named.
 *
 * Four spellings, all equal: `data-event` on the container, `?id=` for every
 * link ever printed, `?slug=`, and the path under /events/ — which is what
 * makes /events/flannel-fest work with no attribute at all, because WordPress
 * rewrites anything under /events that is not a real page onto the shell.
 *
 * An id wins where both are present. Ids are unambiguous; a slug moves when
 * staff hand it to next year's event.
 */
function eventFromPage(
  container: HTMLElement,
  config: WidgetConfig
): { id: number | null; slug: string | null } {
  const q = query();
  const candidates = [
    config.eventId ? String(config.eventId) : undefined,
    attr(container.dataset.event),
    q.get('id') ?? undefined,
    q.get('slug') ?? undefined,
    pathSlug(),
  ];

  let id: number | null = null;
  let slug: string | null = null;
  for (const raw of candidates) {
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isInteger(n) && n > 0) {
      if (id == null) id = n;
      continue;
    }
    const lower = raw.toLowerCase();
    if (slug == null && SLUG.test(lower)) slug = lower;
  }
  return { id, slug };
}

/**
 * The path under /events/, which is the whole slug and not just its last part.
 *
 * `/events/baptism/lake-orion` names one event, so reading only `lake-orion`
 * would ask the API for something else entirely — or for nothing.
 *
 * `details` is excluded because /events/details/ IS the shell: reading its own
 * name as a slug would send every id-based visit looking for an event called
 * "details". A page that is not under /events/ at all falls back to its last
 * segment, which is how a widget dropped on any page still finds its event.
 */
function pathSlug(): string | undefined {
  try {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'events') {
      const rest = parts.slice(1);
      if (!rest.length || rest[0] === 'details') return undefined;
      return rest.join('/');
    }
    const last = parts[parts.length - 1];
    return last && last !== 'details' && last !== 'events' ? last : undefined;
  } catch {
    return undefined;
  }
}

/** The JWT OLG hands back is signed for MP, not for us: read, never trusted. */
function claims(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const body = token.split('.')[1];
    if (!body) return null;
    const payload = body.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function invoiceFromPage(): { guid: string | null; claimedSuccess: boolean } {
  const q = query();
  const fromParam = q.get('invoice');
  const c = claims(q.get('token'));
  const fromToken = typeof c?.invoiceId === 'string' ? c.invoiceId : null;
  const guid = fromParam ?? fromToken;
  const claimedSuccess = q.get('transactionSuccess') === 'true' || c?.transactionSuccess === true;
  return {
    guid: guid && /^[0-9a-f-]{36}$/i.test(guid) ? guid.toLowerCase() : null,
    claimedSuccess,
  };
}

/**
 * How this visit arrived, read once.
 *
 * Read once rather than per render, because the URL is tidied immediately
 * afterwards and a second container rendering later must still see the same
 * answer. Nothing downstream reads the address bar again.
 */
let entry: { guid: string | null; claimedSuccess: boolean } | null = null;
function pageEntry(): { guid: string | null; claimedSuccess: boolean } {
  if (!entry) entry = invoiceFromPage();
  return entry;
}

/**
 * Take OLG's return off the address bar once it has been read.
 *
 * `token` and `transactionSuccess` are single use: the token names the invoice
 * that was just paid and is spent the moment `pageEntry` decodes it. Left
 * behind they are a long, alarming query string on a page somebody is about to
 * bookmark or share, and a reload re-asserts a payment result that MP has long
 * since settled one way or the other.
 *
 * `invoice` is deliberately NOT removed. It only appears when somebody was
 * given a link built for them — `invoiceLink`, or the share button — and on
 * those links it is the whole point. It is also the only thing identifying the
 * page when there is no event id.
 */
function tidyUrl(): void {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has('token') && !u.searchParams.has('transactionSuccess')) return;
    u.searchParams.delete('token');
    u.searchParams.delete('transactionSuccess');
    window.history.replaceState(null, '', u.toString());
  } catch {
    /* no URL or History API: the params are untidy, nothing more */
  }
}

function createDetailsWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  return new ShadowWidget({
    containerIds: config.containerId
      ? [config.containerId]
      : [DEFAULT_CONTAINER_ID, LEGACY_CONTAINER_ID],
    overlayHostId: 'event-details-overlay',
    fontId: 'event-details-widget-montserrat',
    scrollLockFlag: '__EVENT_DETAILS_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Event Details Widget',
    render: (container) => {
      const invoice = pageEntry();
      const event = eventFromPage(container, config);
      return (
        <EventDetails
          apiBaseUrl={apiBaseUrl}
          eventId={event.id}
          eventSlug={event.slug}
          invoiceGuid={invoice.guid}
          claimedSuccess={invoice.claimedSuccess}
          finderUrl={
            config.finderUrl ??
            attr(container.dataset.finderUrl) ??
            attr(container.dataset.returnUrl) ??
            attr(container.dataset.returnurl)
          }
          detailsUrl={config.detailsUrl ?? attr(container.dataset.detailsUrl)}
        />
      );
    },
  });
}

// Where this script was loaded from, for the experiences' art (lib/assets.ts).
// Read now: document.currentScript is only set while the script first runs.
if (typeof document !== 'undefined') {
  const src = (document.currentScript as HTMLScriptElement | null)?.src;
  if (src) setAssetBase(new URL('.', src).href);
}

if (typeof window !== 'undefined') {
  // The API base is baked at build time, never derived from the script's
  // origin: the script serves from widgets.woodsidebible.org, which has no
  // /api routes.
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.EVENT_DETAILS_WIDGET_CONFIG || {}),
  };
  window.EVENT_DETAILS_WIDGET_CONFIG = config;

  if (!window.EventDetailsWidget) {
    const boot = () => {
      // Read the address bar before touching it, then clear OLG's return.
      pageEntry();
      const widget = createDetailsWidget(config);
      widget.init();
      window.EventDetailsWidget = widget;
      tidyUrl();
    };
    // Back from OLG inside the payment popup: hand the result to the page
    // that opened us and close. If the browser will not close a window it
    // did not open, or there is no opener, this is an ordinary return and
    // the page renders here.
    const returned = query().has('token') ? pageEntry() : null;
    const announced =
      returned &&
      announceReturnToOpener({
        invoiceGuid: returned.guid,
        claimedSuccess: returned.claimedSuccess,
      });
    if (announced) {
      setTimeout(() => {
        if (!window.closed && !window.EventDetailsWidget) boot();
      }, 1500);
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
